import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";

const coinIcon = "/items/coin.png";

const formatTimeLeft = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m ${ss}s`;
  if (h > 0) return `${h}h ${m}m ${ss}s`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
};

const FarmingMissionsModal = ({ isOpen, toggle, playerPos, spawnText, npc }) => {
  const [view, setView] = useState("home");
  const [loading, setLoading] = useState(false);
  const [mission, setMission] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);

  useEffect(() => {
    if (!isOpen) return;

    setView("home");
    setMission(null);

    const timer = setInterval(() => {
      setCurrentTime(Date.now() / 1000);
    }, 1000);

    fetchMission();

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchMission = async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/farming/missions/daily", { withCredentials: true });
      setMission(res.data);
    } catch (err) {
      console.error("Failed to fetch farming mission:", err);
      setMission(null);
    } finally {
      setLoading(false);
    }
  };

  const nextResetSeconds = useMemo(() => {
    if (!mission?.next_reset_epoch) return 0;
    return Math.max(0, mission.next_reset_epoch - currentTime);
  }, [mission?.next_reset_epoch, currentTime]);

  const handleReroll = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.post("/v1/farming/missions/reroll", {}, { withCredentials: true });
      setMission(res.data);
      spawnText?.(
        `Mission rerolled (-${res.data?.reroll?.cost_coins || 500} Coins)`,
        playerPos?.x || 0,
        (playerPos?.y || 0) - 20,
        "#FFD700"
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to reroll mission";
      spawnText?.(msg, playerPos?.x || 0, (playerPos?.y || 0) - 20, "#f44336");
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.post("/v1/farming/missions/claim", {}, { withCredentials: true });
      setMission(res.data?.mission || mission);
      const rew = res.data?.mission?.reward;
      if (rew) {
        const berryName = rew?.berry?.name || "berry";
        const berryAmt = Number(rew?.berry?.amount || 0);
        spawnText?.(
          `Claimed +${rew.farming_exp} Farming EXP and +${berryAmt} ${berryName}!`,
          playerPos?.x || 0,
          (playerPos?.y || 0) - 20,
          "#4CAF50"
        );
      } else {
        spawnText?.("Reward claimed!", playerPos?.x || 0, (playerPos?.y || 0) - 20, "#4CAF50");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to claim reward";
      spawnText?.(msg, playerPos?.x || 0, (playerPos?.y || 0) - 20, "#f44336");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const canReroll = !!mission?.reroll?.is_allowed && !loading;
  const canClaim = !!mission?.is_completed && !mission?.is_claimed && !loading;

  const rewardBerryImg = mission?.reward?.berry?.image ? `/${mission.reward.berry.image}` : null;

  return (
    <div
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 10px",
        boxSizing: "border-box",
        zIndex: 1050,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "95%",
          maxWidth: 720,
          maxHeight: "95vh",
          background: "linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))",
          color: "#fff",
          borderRadius: 12,
          backdropFilter: "blur(10px)",
          border: "2px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          margin: "10px",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(0, 0, 0, 0.2)",
            flexWrap: "wrap",
            gap: "8px",
            minHeight: "60px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 auto" }}>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Farming Missions
            </h3>
            <div style={{ opacity: 0.85, fontSize: 13 }}>{npc?.name || "Berry Mission Officer"}</div>
          </div>

          <button
            onClick={toggle}
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "none",
              color: "#fff",
              borderRadius: "4px",
              padding: "6px 12px",
              cursor: "pointer",
              transition: "all 0.2s",
              fontSize: "14px",
            }}
            onMouseOver={(e) => (e.target.style.background = "rgba(255, 255, 255, 0.2)")}
            onMouseOut={(e) => (e.target.style.background = "rgba(255, 255, 255, 0.1)")}
          >
            Close
          </button>
        </div>

        <div style={{ padding: "16px", overflowY: "auto", flex: 1 }}>
          {loading && !mission ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: "10px" }}>Loading mission...</p>
            </div>
          ) : (
            <>
              {view === "home" ? (
                <div
                  style={{
                    background: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <img
                      src={npc?.sprite || "/Trainers/berry2.png"}
                      alt={npc?.name || "Farming"}
                      style={{ width: 64, height: 64, imageRendering: "pixelated" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/Trainers/berry2.png";
                      }}
                    />
                    <div style={{ flex: "1 1 240px" }}>
                      <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.95 }}>
                        {npc?.dialogue ||
                          "I’ll give you a farming mission every day at 6am. Rewards depend on how many berries you harvest."}
                      </div>
                      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
                        Next mission in: <strong>{formatTimeLeft(nextResetSeconds)}</strong>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                    <button
                      onClick={() => setView("mission")}
                      style={{
                        background: "linear-gradient(to bottom, #4a5568, #2d3748)",
                        border: "none",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "10px 16px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      Missions
                    </button>
                    <button
                      onClick={fetchMission}
                      disabled={loading}
                      style={{
                        background: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.15)",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "10px 16px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: 700,
                        opacity: loading ? 0.6 : 1,
                      }}
                    >
                      {loading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                    <button
                      onClick={() => setView("home")}
                      style={{
                        background: "rgba(255, 255, 255, 0.1)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        color: "#fff",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      Back
                    </button>
                  </div>

                  <div
                    style={{
                      background: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#86efac" }}>
                          {mission?.mission_title || "Daily Mission"}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 13, opacity: 0.9 }}>
                          Next mission in: <strong>{formatTimeLeft(nextResetSeconds)}</strong>
                        </div>
                      </div>

                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                        <div>
                          Status:{" "}
                          {mission?.is_claimed
                            ? "Claimed"
                            : mission?.is_completed
                              ? "Completed"
                              : "In Progress"}
                        </div>
                        <div>Tier: {mission?.tier || ""}</div>
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <div
                        style={{
                          height: 10,
                          background: "rgba(255, 255, 255, 0.12)",
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width:
                              mission?.target > 0
                                ? `${Math.min(100, Math.round((mission.progress / mission.target) * 100))}%`
                                : "0%",
                            background: mission?.is_completed ? "#4CAF50" : "#86efac",
                            transition: "width 0.2s",
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, opacity: 0.9 }}>
                        Progress: <strong>{mission?.progress || 0}</strong> / <strong>{mission?.target || 0}</strong>
                      </div>
                    </div>

                    <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <div
                        style={{
                          flex: "1 1 260px",
                          background: "rgba(0, 0, 0, 0.25)",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <div style={{ fontWeight: 800, marginBottom: 8 }}>Rewards</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <span style={{ opacity: 0.9 }}>Farming EXP:</span>
                          <strong>{mission?.reward?.farming_exp || 0}</strong>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ opacity: 0.9 }}>Berry:</span>
                          <strong>{mission?.reward?.berry?.amount || 0}</strong>
                          <span style={{ opacity: 0.9 }}>{mission?.reward?.berry?.name || ""}</span>
                          {rewardBerryImg && (
                            <img
                              src={rewardBerryImg}
                              alt={mission?.reward?.berry?.name || "Berry"}
                              style={{ width: 18, height: 18, imageRendering: "pixelated" }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                        </div>
                      </div>

                      <div style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 10 }}>
                        <button
                          onClick={handleClaim}
                          disabled={!canClaim}
                          style={{
                            background: canClaim ? "rgba(76, 175, 80, 0.25)" : "rgba(74, 85, 104, 0.3)",
                            border: canClaim ? "1px solid rgba(76, 175, 80, 0.5)" : "1px solid rgba(255, 255, 255, 0.12)",
                            color: "#fff",
                            padding: "10px 16px",
                            borderRadius: "4px",
                            cursor: canClaim ? "pointer" : "not-allowed",
                            fontSize: "14px",
                            fontWeight: 800,
                          }}
                        >
                          {mission?.is_claimed ? "Claimed" : loading ? "Processing..." : "Claim Reward"}
                        </button>

                        <button
                          onClick={handleReroll}
                          disabled={!canReroll}
                          style={{
                            background: canReroll ? "rgba(255, 193, 7, 0.18)" : "rgba(74, 85, 104, 0.3)",
                            border: canReroll ? "1px solid rgba(255, 193, 7, 0.35)" : "1px solid rgba(255, 255, 255, 0.12)",
                            color: "#fff",
                            padding: "10px 16px",
                            borderRadius: "4px",
                            cursor: canReroll ? "pointer" : "not-allowed",
                            fontSize: "14px",
                            fontWeight: 800,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                          }}
                        >
                          Change Mission ({mission?.reroll?.cost_coins || 500}
                          <img
                            src={coinIcon}
                            alt="Coins"
                            style={{ width: 18, height: 18, imageRendering: "pixelated" }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = "none";
                            }}
                          />
                          )
                        </button>

                        {mission?.is_completed && !mission?.is_claimed && (
                          <div
                            style={{
                              background: "rgba(76, 175, 80, 0.12)",
                              border: "1px solid rgba(76, 175, 80, 0.22)",
                              borderRadius: 8,
                              padding: 10,
                              fontSize: 13,
                              color: "#b7ffb7",
                              fontWeight: 700,
                              textAlign: "center",
                            }}
                          >
                            Mission complete! Claim your reward.
                          </div>
                        )}

                        {mission?.is_claimed && (
                          <div
                            style={{
                              background: "rgba(255, 255, 255, 0.06)",
                              border: "1px solid rgba(255, 255, 255, 0.12)",
                              borderRadius: 8,
                              padding: 10,
                              fontSize: 13,
                              opacity: 0.9,
                              fontWeight: 700,
                              textAlign: "center",
                            }}
                          >
                            Reward claimed. Come back for the next mission!
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FarmingMissionsModal;
